import { useState } from 'react';
import { SCOPE_TYPES } from '@asyagrup/group-hub-contracts';
import { useSession } from '@/app/session-context';
import { Field, Select } from './primitives';

/**
 * Yeni kaydın hangi şirkete yazılacağı.
 *
 * Sunucu tarafında `resolveWriteCompanyId` şu kuralı uyguluyor: kapsam tek
 * şirketse onu kullan, birden çoksa gövdeden gelen seçimi bekle, "hepsine yaz"
 * diye bir cevabı kabul etme. Kuralın kendisi doğru — ama seçimi soran bir alan
 * hiç yazılmamıştı, dolayısıyla kapsamı birden çok şirketi içeren bir kullanıcı
 * Misafirhane'de hiçbir kayıt açamıyordu: form 400 dönüyor ve "Kayıt hangi
 * şirkete ait olacak, seçilmeli" diyordu, ama seçilecek yer yoktu.
 *
 * Alan yalnızca gerçekten bir seçim varken çizilir. Tek şirketli kullanıcıya
 * cevabı belli bir soru sormak, formu uzatmaktan başka bir şey yapmaz.
 */
export interface CompanyChoice {
  companies: Array<{ id: string; code: string; name: string }>;
  /**
   * Alan çizilir ve cevaplanmadan form gönderilemez: kapsam birden çok
   * şirketse, ya da «Grup geneli» de bir cevapsa.
   */
  required: boolean;
  /** Seçilen şirket; «Grup geneli» seçildiyse de boş. */
  companyId: string;
  setCompanyId: (id: string) => void;
  /** Listede «Grup geneli» satırı var — yalnız isteyen formda ve GRUP kapsamında. */
  groupWideAllowed: boolean;
  /** Kişi «Grup geneli»ni AÇIKÇA seçti. */
  groupWide: boolean;
}

/**
 * «Grup geneli» satırının seçim değeri. Uuid olamayacağı için hiçbir şirketle
 * karışmaz, ve sunucuya hiç gitmez: `companyId` onu boş okur.
 */
const GROUP_WIDE = 'grup-geneli';

/**
 * Seçim listesinde bir şirketin nasıl yazılacağı.
 *
 * Ad tek başına yetmiyor, çünkü ad DEĞİŞİYOR. DST-00077: kullanıcı mükellef
 * kartını açarken listede "ASYA ASC" aradı, bulamadı ve kaydı yanlış şirkete
 * yazdı — o saatte şirketin adı sistemde "Yedek Parça"ydı ve birkaç saat sonra
 * düzeltildi. Yanlış olan kullanıcının araması değildi; liste, aradığı şeyi
 * göstermiyordu.
 *
 * Kod bu yüzden var: adın aksine sabit, entegrasyon telinde `unitCode` olarak
 * zaten geçiyor ve kullanıcı şirketi çoğu zaman onunla anıyor. İkisini birden
 * göstermek, adın o gün ne olduğundan bağımsız olarak kaydın doğru şirkete
 * gitmesini sağlıyor.
 */
export function sirketEtiketi(sirket: { code: string; name: string }): string {
  return `${sirket.name} (${sirket.code})`;
}

/**
 * `allowGroupWide`: kaydın kendisi grup geneli olabiliyorsa (`company_id`
 * NULL = "grubun" — duyuru kategorisi gibi). Varsayılan KAPALI ve öyle
 * kalmalı: başka her ekranda boş şirket "yanlış şirkete yazma" demek.
 *
 * Açıkken bile boş bırakmak grup geneli DEĞİL. Alanın gerekçesi aşağıda —
 * ön seçim yok, boş değer reddediliyor — ve grup geneli de bir cevap olarak
 * listede duruyor: dört şirketin hepsine birden yazmak, birine yazmaktan daha
 * az değil daha çok bir karar. Satır yalnız GRUP kapsamına çıkıyor, çünkü
 * sunucu (`assertCompanyScope`) grup geneli yazımı ancak ona açıyor;
 * basılamayacak bir seçenek göstermek 403'ü forma taşımaktan öte bir şey
 * yapmaz.
 */
export function useCompanyChoice({
  allowGroupWide = false,
}: { allowGroupWide?: boolean } = {}): CompanyChoice {
  const { user } = useSession();
  /*
   * `user.companies` kapsamdan çözülmüş liste — sözleşmedeki tanımıyla
   * "principal'ın işlem yapabileceği şirketler". Sunucunun `tenantScope
   * .companyIds`iyle aynı kaynaktan gelir, o yüzden buradaki "bir mi, çok mu"
   * kararı sunucununkiyle ayrışmaz. GRUP kapsamı da öyle: sunucunun
   * `tenantScope.group`u aynı `scopes` listesinden türüyor.
   */
  const companies = user?.companies ?? [];
  const groupWideAllowed =
    allowGroupWide && (user?.scopes.some((s) => s.type === SCOPE_TYPES.GROUP) ?? false);
  const [selection, setSelection] = useState('');
  const groupWide = groupWideAllowed && selection === GROUP_WIDE;

  return {
    companies,
    required: companies.length > 1 || groupWideAllowed,
    companyId: selection === GROUP_WIDE ? '' : selection,
    setCompanyId: setSelection,
    groupWideAllowed,
    groupWide,
  };
}

/**
 * Grup geneli olabilen bir kayda yazılacak şirket: `null` = grubun.
 *
 * Bu kayıtların sunucusu tek şirketi kendisi SEÇMİYOR (`resolveWriteCompanyId`
 * değil): `null`u "grup geneli" diye okuyup GRUP kapsamı istiyor. Yani alanı
 * hiç görmeyen tek şirketli kullanıcı adına `null` göndermek, onun adına grup
 * geneli kayıt istemekti ve 403 dönüyordu — formda düzeltecek bir alan da
 * yoktu. Alan çizilmediyse soru zaten cevaplıdır: tek şirket.
 */
export function writeCompanyId(choice: CompanyChoice): string | null {
  if (choice.groupWide) return null;
  if (choice.companyId) return choice.companyId;
  if (!choice.required && choice.companies.length === 1) return choice.companies[0]!.id;
  return null;
}

/**
 * `idPrefix` formdaki diğer alanların kimlik düzenini takip eder ("room" →
 * `room-company`), böylece aynı sayfada iki form açıkken etiketler birbirinin
 * alanına bağlanmaz.
 */
export function CompanyField({
  choice,
  idPrefix,
  hint = 'Kayıt bu şirkete yazılır ve sonradan taşınamaz.',
}: {
  choice: CompanyChoice;
  idPrefix: string;
  hint?: string;
}) {
  if (!choice.required) return null;

  const id = `${idPrefix}-company`;

  return (
    <Field label="Şirket" htmlFor={id} hint={hint}>
      <Select
        id={id}
        required
        value={choice.groupWide ? GROUP_WIDE : choice.companyId}
        onChange={(e) => choice.setCompanyId(e.target.value)}
      >
        {/* Ön seçim yok: dört şirketi olan birine ilkini seçilmiş göstermek,
            yanlış şirkete yazmayı bir tıkla mümkün kılar. `required` boş değeri
            reddeder, yani soru cevaplanmadan form gönderilemez. */}
        <option value="">Seçin</option>
        {choice.groupWideAllowed && <option value={GROUP_WIDE}>Grup geneli (tüm şirketler)</option>}
        {choice.companies.map((c) => (
          <option key={c.id} value={c.id}>
            {sirketEtiketi(c)}
          </option>
        ))}
      </Select>
    </Field>
  );
}
